<template>
  <div :class="$style.interview_page">
    <OrganismsLoading v-show="isLoading" />
    <template v-if="coverRef">
      <AtomsConsumerImage
        v-if="md && coverRef.list[0].pc_img_url"
        :src="coverRef.list[0].pc_img_url"
        :class="$style.pc_img"
        :width="1280"
      />
      <AtomsConsumerImage
        v-if="!md && coverRef.list[0].sp_img_url"
        :src="coverRef.list[0].sp_img_url"
        :class="$style.sp_img"
        :width="768"
      />
    </template>
    <div :class="$style.content">
      <div :class="$style.interview">
        <span>Interview</span>
      </div>
      <!-- 概要 -->
      <PagePartsEditorText
        v-if="descriptionRef"
        :text="descriptionRef.detail.title ?? ''"
        :sp-text="descriptionRef.detail.sp_title"
      />
      <!-- 目次 -->
      <PagesInterviewTableOfContents :page-parts="pagePartsForTableOfContents" />
      <!-- 内容 -->
      <PagePartsSet :page-parts="pagePartsRef" />
      <!-- 監修者 & シェア -->
      <PagesInterviewSupervisorProfile v-if="supervisorProfile.name" v-bind="supervisorProfile" />
      <OrganismsShare v-bind="shareInfo" />
      <PagesInterviewNavigationLinks :class="$style.navigation" list-link="/interviews" />
      <!-- おすすめ商品 -->
      <PagePartsEditorText
        v-if="recommendProductRef.title"
        :text="recommendProductRef.title.detail.title ?? ''"
        :sp-text="recommendProductRef.title.detail.sp_title"
      />
      <PagePartsRecommendProductsV2
        v-if="recommendProductRef.products"
        :products="recommendProductRef.products"
        :class="$style.recommend_products"
      />
    </div>
    <div :class="$style.footer_content">
      <PagesInterviewRelatedInterviews :interviews="interviewList" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { PagePartsFragmentFragment } from "~/types/pageParts"
import {
  usePublicRecommendInterviewPagesQuery,
  usePublicProductFindManyQuery,
  usePublicProductCategoryFindOneQuery,
} from "~/gql/urql.generated"
import {
  Product,
  PublicProductFindManyQueryVariables,
  PublicProductCategoryFindOneQueryVariables,
  ProductCategory,
} from "~/types/type.generated"
import { createArticleJsonLD, useCustomMeta, createBreadcrumbJsonLD } from "~/composables/meta"
import { ScriptPropertySrc, ScriptPropertyText } from "~/types/meta"
import { ApiPageOneAbsoluteUrlResponsePage } from "~/types/server/api/page"
import { useSocial } from "~/composables/useSocial"

definePageMeta({ layout: "default" })

const { width: windowWidth } = useWindowSize()
const md = computed(() => windowWidth.value >= 768)
// レイアウト用にpagePartsから抜き出すパーツ
const coverRef = ref<PagePartsFragmentFragment>()
const interviewListPart = ref<PagePartsFragmentFragment>()
const descriptionRef = ref<PagePartsFragmentFragment>()
const recommendProductRef = ref<{ title?: PagePartsFragmentFragment; products?: Product[] }>({
  title: undefined,
  products: undefined,
})
// 目次用
const pagePartsForTableOfContents = computed(() => {
  if (!pagePartsRef.value) return []
  const result = [...pagePartsRef.value]
  if (recommendProductRef.value.title) {
    result.push(recommendProductRef.value.title)
  }
  return result
})

const route = useRoute()
const customMeta = useCustomMeta()
const config = useRuntimeConfig()
const pageRef = ref<ApiPageOneAbsoluteUrlResponsePage>()
const pagePartsRef = ref<PagePartsFragmentFragment[]>([])
const isLoading = useState<boolean>(() => true)
const siteId = useSite().getSiteId()
const isMobile = ref(true)

const supervisorProfile = computed(() => ({
  imageUrl: pageRef.value?.supervisor?.icon_image ?? "",
  name: pageRef.value?.supervisor?.name ?? "",
  nameAlphabet: pageRef.value?.supervisor?.family_name_en ?? "",
  belongName: pageRef.value?.supervisor?.belong ?? "",
  positionName: pageRef.value?.supervisor?.position ?? "",
  description: pageRef.value?.supervisor?.description ?? "",
}))

const shareInfo = computed(() => ({
  title: pageRef.value?.title ?? "",
  url: config.public.BASE_URL + route.fullPath,
}))

const { fetchWithHandleRedirect } = usePageOneApi()
const absoluteUrl = `interviews/${route.params.pageSlug}`
const pageOneResponse = await fetchWithHandleRedirect(absoluteUrl, route)

pageRef.value = pageOneResponse.page
// TODO: 型修正
pagePartsRef.value = pageOneResponse.page_parts as PagePartsFragmentFragment[]

const productCategoryVariables = reactive<PublicProductCategoryFindOneQueryVariables>({
  filter: {
    document_id: "",
  },
})
const { executeQuery: executeQueryProductCategoryFindOne } = await usePublicProductCategoryFindOneQuery({
  requestPolicy: "cache-first",
  variables: productCategoryVariables,
  pause: true,
})

const productListVariablesRef = ref<PublicProductFindManyQueryVariables>({})
const { executeQuery: executeProductListQuery } = await usePublicProductFindManyQuery({
  requestPolicy: "cache-first",
  variables: productListVariablesRef,
  pause: true,
})

const pagePartsFormat = async () => {
  if (!pagePartsRef.value) return
  const maybeImageSlide = pagePartsRef.value[0]
  if (maybeImageSlide && maybeImageSlide.layout_type === "image_slide_big") {
    // ページトップのバナーは２列表示対象外であるため、抜き出す
    coverRef.value = maybeImageSlide
    pagePartsRef.value = pagePartsRef.value.slice(1)
  }

  const maybeEditorTextAsDescription = pagePartsRef.value[0]
  if (maybeEditorTextAsDescription && maybeEditorTextAsDescription.layout_type === "editor_text") {
    // SPで概要直後に目次を挿入したいため、抜き出す
    descriptionRef.value = maybeEditorTextAsDescription
    pagePartsRef.value = pagePartsRef.value.slice(1)
  }

  const recommendProductParts = pagePartsRef.value.find(
    (pagePart) => pagePart.layout_type === "recommend_product_list_v2",
  )
  const maybeRecommendProductTitle = pagePartsRef.value[pagePartsRef.value.length - 2]

  let globalIndex = 1 // ページが複数ある場合idを重複させないため
  pagePartsRef.value = pagePartsRef.value.map((pagePart) => {
    const pattern = /<h2(.+?)>/g
    const title = pagePart.detail.title
    if (pagePart.layout_type === "editor_text" && title) {
      const matches = []
      let match
      while ((match = pattern.exec(title))) {
        matches.push(match)
      }
      if (matches.length !== 0) {
        let headResult = title.substring(0, matches[0].index - 1)
        for (let i = 0; i < matches.length; i++) {
          const index = matches[i].index
          const nextIndex = i < matches.length - 1 ? matches[i + 1].index : title.length
          const tmpStr = title.substring(index, nextIndex)
          headResult += `${tmpStr.replace(pattern, `<h2 id='heading${globalIndex++}'$1>`)}`
        }
        pagePart.detail.title = headResult
      }
    }
    return pagePart
  })
  if (recommendProductParts) {
    productListVariablesRef.value = {
      filter: {
        OR: recommendProductParts.product_ids.map((productId) => ({
          document_id: productId,
        })),
      },
    }
    if (executeProductListQuery().isPaused) await executeProductListQuery().resume()
    const products = await executeProductListQuery()
    recommendProductRef.value.products = (products.data.value?.publicProductFindMany as Product[]) || []
    pagePartsRef.value = pagePartsRef.value.slice(0, pagePartsRef.value.length - 2)

    // SPでおすすめ商品直前に監修者プロフィール、シェアを挿入したいため、抜き出す
    if (maybeRecommendProductTitle) {
      recommendProductRef.value.title = maybeRecommendProductTitle
    }
  }
}

const interviewIds = computed(() => {
  return interviewListPart.value?.interview_page_ids
})
// Interview
const variablesOtherInterview = computed(() => {
  return {
    siteId,
    exceptPageId: pageRef.value?.document_id,
    interviewIds: interviewIds.value,
  }
})
const { data: otherInterviewsResult } = usePublicRecommendInterviewPagesQuery({ variables: variablesOtherInterview })

const interviewList = computed(() => {
  const list = otherInterviewsResult.value?.publicRecommendInterviewPages?.items || []
  if (!interviewIds.value || interviewIds.value.length === 0) {
    return list
  }
  list.sort((a, b) => interviewIds.value!.indexOf(a.document_id) - interviewIds.value!.indexOf(b.document_id))
  return list
})

const { marketingTagsEvent } = useSocial()

// page取得後にpagePartsの取得
if (pageRef.value) {
  await useAsyncData(
    "usePublicPagePartsManyQuery",
    async () => {
      isLoading.value = true
      const { buildProductIds, fetchProductFindMany, updatePagePartsWithProducts } = useFetchProductForPageParts()
      const productIds = buildProductIds(pagePartsRef.value)
      if (productIds.length) {
        const products = await fetchProductFindMany(productIds)
        updatePagePartsWithProducts(pagePartsRef.value, products)
      }
      marketingTagsEvent(pagePartsRef.value)

      // fetch page parts product
      if (pagePartsRef.value.length > 0) {
        for await (const pageParts of pagePartsRef.value) {
          if (pageParts.category_id) {
            // fetch category
            productCategoryVariables.filter = {
              document_id: pageParts.category_id,
              is_status: true,
            }

            if (executeQueryProductCategoryFindOne().isPaused) await executeQueryProductCategoryFindOne().resume()
            const { data } = await executeQueryProductCategoryFindOne()

            if (data?.value?.publicProductCategoryFindOne) {
              pageParts.category = data?.value?.publicProductCategoryFindOne as ProductCategory
            }
          }
        }
      }

      isLoading.value = false
    },
    { lazy: true },
  )
}

const originOverflowX = ref("")

onMounted(async () => {
  await pagePartsFormat()
  originOverflowX.value = document.body.style.overflowX
  document.body.style.overflowX = "visible" // base.scssでhiddenとなっており、stickyが効かないためvisibleに変更
  isMobile.value = window.innerWidth < 768
  isLoading.value = false
})

onUnmounted(() => {
  document.body.style.overflowX = originOverflowX.value // もとに戻す
})

const metaData = computed(() => {
  const title = pageRef.value?.title || ""
  const description = pageRef.value?.description || ""
  const image = pageRef.value?.ogp_url || ""
  return {
    title,
    description,
    image,
  }
})
const metaScript = computed(() => {
  const script: (ScriptPropertyText | ScriptPropertySrc)[] = []
  const felmatTag = { src: "https://js.crossees.com/csslp.js" }
  const linkATag = { src: "https://link-ag.net/dist/p/l/index.js" }
  script.push(felmatTag)
  script.push(linkATag)
  return script
})

useHead(
  customMeta.fetch({
    title: `${metaData.value.title}`,
    description: `${metaData.value.description}`,
    image: `${metaData.value.image}`,
    script: metaScript.value,
    robots: pageRef.value?.noindex ? "noindex" : undefined,
    jsonLDInfo: [
      createArticleJsonLD({
        headline: pageRef.value?.title,
        image: pageRef.value?.ogp_url,
        description: pageRef.value?.description,
      }),
      createBreadcrumbJsonLD([
        {
          title: "インタビュー一覧",
          url: "/interviews",
        },
        {
          title: pageRef.value?.title,
          url: route.path,
        },
      ]),
    ],
  }),
)
</script>
<style module lang="scss">
$editor-text-padding-size-pc: 56px;
$editor-text-padding-size-tablet: 40px;
$editor-text-padding-size-sp: 24px;
$editor-text-padding-pc: 0 0 0 $editor-text-padding-size-pc;

.interview_page {
  .pc_img {
    width: 100%;
  }
  .sp_img {
    width: 100%;
  }
  .content {
    @include full-container-lg;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin: 2rem auto;
    .interview {
      @include container-md;
      span {
        @include main-font;
        font-size: $font-size-13;
        color: $primary;
        border-bottom: 1px solid $primary;
      }
    }
    :global {
      h1 {
        font-size: $font-size-24;
        color: $primary;
        @include md {
          font-size: $font-size-32;
        }
      }
      h2 {
        font-size: $font-size-20;
        color: $primary;
        padding: 1rem 0;
        border-bottom: 1px solid $primary;
        @include md {
          font-size: $font-size-26;
        }
      }
      p {
        font-size: $font-size-15;
        line-height: 2;
        @include md {
          font-size: $font-size-16;
        }
      }
      img {
        max-width: 100%;
      }
    }
  }
  .footer_content {
    @include container-lg;
  }
}
</style>
